import React from 'react';
import { Card, Row, Col, Badge, Button, Alert } from 'react-bootstrap';
import { FaSpinner, FaList, FaBrain, FaWrench, FaSync, FaStop } from 'react-icons/fa';
import Spinner from '../Spinner';
import { getStatusBadgeVariant, formatDuration, safeRenderValue } from './utils/logsUtils';

const ExecutionStatusCard = ({
  executionArn,
  bodyMessage,
  bodyExecutionArn,
  executionStatus,
  fetchingStatus,
  statusPollingActive,
  fetchingLambdaLogs,
  fetchingAnalysis,
  stoppingExecution,
  restoredFromLogs,
  onFetchLambdaLogs,
  onAnalyzeExecution,
  onQuickEditClick,
  onStopExecution,
  onManualRefresh
}) => {
  if (!executionArn && !bodyMessage && !bodyExecutionArn && !restoredFromLogs) {
    return null;
  }

  return (
    <Card className="execution-status-card">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h2 className="section-heading mb-0">
            Execution Status
            {fetchingStatus && statusPollingActive && <FaSpinner className="fa-spin ms-2" />}
          </h2>
          
          <div className="d-flex gap-2 flex-wrap">
            <Button 
              variant="info" 
              size="sm"
              onClick={onFetchLambdaLogs}
              disabled={fetchingLambdaLogs || !executionArn}
              className="d-flex align-items-center"
            >
              {fetchingLambdaLogs ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Loading...
                </>
              ) : (
                <>
                  <FaList className="me-2" />
                  Lambda Logs
                </>
              )}
            </Button>

            <Button 
              variant="warning" 
              size="sm"
              onClick={onAnalyzeExecution}
              disabled={fetchingAnalysis || !executionArn}
              className="d-flex align-items-center"
            >
              {fetchingAnalysis ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Starting...
                </>
              ) : (
                <>
                  <FaBrain className="me-2" />
                  Analyze
                </>
              )}
            </Button>

            <Button 
              variant="success" 
              size="sm"
              onClick={onQuickEditClick}
              disabled={!executionArn && !restoredFromLogs}
              className="d-flex align-items-center"
            >
              <FaWrench className="me-2" />
              Quick Edit
            </Button>

            {restoredFromLogs && (
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={onManualRefresh}
                disabled={fetchingStatus}
                title="Refresh to get the latest execution state"
              >
                {fetchingStatus ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <FaSync className="me-2" />
                    Refresh
                  </>
                )}
              </Button>
            )}

            {executionArn && statusPollingActive && 
             executionStatus?.status !== 'SUCCEEDED' && 
             executionStatus?.status !== 'FAILED' && (
              <Button 
                variant="danger" 
                size="sm"
                onClick={onStopExecution}
                disabled={stoppingExecution}
                title="Stop the current execution"
              >
                {stoppingExecution ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Stopping...
                  </>
                ) : (
                  <>
                    <FaStop className="me-2" />
                    Stop
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {(bodyMessage || bodyExecutionArn) && (
          <div className="execution-result-fields mb-3">
            <Row className="mb-3">
              {bodyMessage && (
                <Col md={12} className="mb-3">
                  <div className="info-field">
                    <div className="info-field-label">
                      <strong>Status Message:</strong>
                    </div>
                    <div className="info-field-value">
                      <div className="bg-light p-3 rounded border">
                        {safeRenderValue(bodyMessage)}
                      </div>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}
        
        {/* FIXED: Status row with proper badge nowrap styling */}
        {executionStatus && (
          <Row className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap' }}>
            <Col md={4}>
              <div className="info-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                <strong style={{ flexShrink: 0 }}>Status: </strong>
                <Badge 
                  bg={getStatusBadgeVariant(executionStatus?.status)}
                  style={{ 
                    whiteSpace: 'nowrap',
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 10px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    flexShrink: 0,
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px'
                  }}
                >
                  {executionStatus?.status || 'RUNNING'}
                </Badge>
              </div>
            </Col>
            
            <Col md={4}>
              <div className="info-item">
                <strong>Start: </strong>
                <span className="text-muted ms-2">
                  {executionStatus?.startDate ? new Date(executionStatus.startDate).toLocaleString() : 'Pending...'}
                </span>
              </div>
            </Col>
            
            <Col md={4}>
              <div className="info-item">
                <strong>Duration: </strong>
                <span className="text-muted ms-2">
                  {executionStatus?.startDate && executionStatus?.stopDate 
                    ? formatDuration(executionStatus.startDate, executionStatus.stopDate)
                    : executionStatus?.duration ? `${executionStatus.duration}s` : 'In progress...'}
                </span>
              </div>
            </Col>
          </Row>
        )}
        
        {executionStatus?.status === 'FAILED' && (
          <Alert variant="danger" className="mt-3">
            <strong>Execution Failed</strong>
            {executionStatus.error && (
              <div className="mt-2">
                <strong>Error:</strong> {safeRenderValue(executionStatus.error)}
              </div>
            )}
          </Alert>
        )}
        
        {executionStatus?.status === 'SUCCEEDED' && (
          <Alert variant="success" className="mt-3">
            <strong>Execution Completed Successfully</strong>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};

export default ExecutionStatusCard;